import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { messageLinks, type MessageLink } from "../links";
import { navigate } from "../router";
import type { LinkPreview, Message } from "../sync/types";
import { RichText } from "../text";
import { Avatar } from "./ui";
import { locale, t } from "../i18n";

// LinkPreviews quotes the Lantern messages a message links to, for those
// who can read them; others see only that there is one.
export function LinkPreviews({ text, self }: { text: string; self: string }) {
  const links = messageLinks(text, location.origin).filter((l) => l.message !== self);
  if (links.length === 0) return null;
  return (
    <div className="link-previews">
      {links.map((l) => (
        <Preview key={l.message} link={l} />
      ))}
    </div>
  );
}

function Preview({ link }: { link: MessageLink }) {
  const session = useSession();
  const state = useLantern();
  // A message already here is shown as it is now.
  const here = state.channels.get(link.channel)?.messages.get(link.message);
  const [fetched, setFetched] = useState<Message | null | undefined>(undefined);
  useEffect(() => {
    if (here) return;
    let live = true;
    void session.linkedMessage(link.channel, link.message).then((m) => live && setFetched(m));
    return () => {
      live = false;
    };
  }, [session, link.channel, link.message, !!here]);
  const m = here ?? fetched;
  if (m === undefined) return null;
  if (m === null) {
    return <p className="link-preview none">{t("A message in a channel you are not in, or one since deleted.")}</p>;
  }
  return <Quote m={m} />;
}

// Quote shows a message as a link to it quotes it.
export function Quote({ m }: { m: Message }) {
  const session = useSession();
  const state = useLantern();
  const open = () => navigate(m.thread_root_id ? `/c/${m.channel_id}/t/${m.thread_root_id}` : `/c/${m.channel_id}/m/${m.id}`);
  const where = {
    channel: session.channelName(m.channel_id),
    when: new Date(m.created_at).toLocaleString(locale, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
  };
  return (
    <blockquote className="link-preview">
      <button className="link-preview-head" onClick={open} aria-label={t("Open {name}'s message", { name: session.name(m.author.id) })}>
        <Avatar id={m.author.id} name={session.name(m.author.id)} size={20} />
        <strong>{session.name(m.author.id)}</strong>
        <span className="muted">{m.thread_root_id ? t("in {channel} · thread · {when}", where) : t("in {channel} · {when}", where)}</span>
      </button>
      <div className="link-preview-text">
        <RichText text={m.text || (m.files?.length ? t("a file") : "")} name={(id) => session.name(id)} me={state.me?.id} group={(id) => session.groupMention(id)} />
      </div>
    </blockquote>
  );
}

const outside = /https?:\/\/[^\s<>"|)\]]+/;

// PagePreviews shows what the pages a message links to say about
// themselves, as the server read them; links to Lantern itself are quoted
// above instead.
export function PagePreviews({ m }: { m: Message }) {
  const session = useSession();
  const [list, setList] = useState<LinkPreview[]>([]);
  const has = outside.test(m.text.replaceAll(location.origin + "/", ""));
  useEffect(() => {
    if (!has || m.class === "ephemeral") return;
    let live = true;
    void session.linkPreviews(m).then((p) => live && setList(p));
    return () => {
      live = false;
    };
  }, [session, m.id, m.edited_at, has]);
  if (list.length === 0) return null;
  return (
    <div className="page-previews">
      {list.map((p) => (
        <a key={p.url} className="page-preview" href={p.url} target="_blank" rel="noopener noreferrer">
          {p.site && <span className="page-site">{p.site}</span>}
          <strong className="page-title">{p.title}</strong>
          {p.description && <span className="page-description">{p.description}</span>}
        </a>
      ))}
    </div>
  );
}
